import React, {
  CSSProperties,
  useContext,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Helmet } from 'react-helmet-async';
import { ArrowRightOutlined, UndoOutlined } from '@ant-design/icons';

import {
  Button,
  Card,
  Empty,
  Input,
  Result,
  Skeleton,
  Space,
  Tag,
  Tooltip,
  Typography,
} from 'antd';

import DataChangeRequestStatus, {
  DataChangeRequestValue,
} from '../components/dataChangeRequests/DataChangeRequestStatus';
import EditableDataChangeRequestLogo from '../components/dataChangeRequests/EditableDataChangeRequestLogo';
import ColorInput from '../components/utils/ColorInput';
import {
  GetClientOrganizationDetailsQuery,
  useCreateClientDataChangeRequestMutation,
  useDeleteClientDataChangeRequestMutation,
  useGetClientOrganizationDetailsQuery,
  useUpdateClientDataChangeRequestMutation,
} from '../graphql/generated';
import { UserContext } from '../contexts/UserContext';
import {
  draftToNetworkMergeVarChanges,
  EditableNetworkMergeVar,
  EDITABLE_NETWORK_MERGE_VARS,
  getLiveNetworkMergeVarValues,
  getNetworkMergeVarBaseline,
  getPendingNetworkRequest,
  NetworkMergeVarDraft,
  networkMergeVarChangesEqual,
  networkMergeVarDraftsEqual,
  originalValuesForNetworkMergeVarChanges,
  requestToNetworkMergeVarChanges,
} from './clientNetworkChanges';

type Organization = NonNullable<
  NonNullable<GetClientOrganizationDetailsQuery['users_by_pk']>
>['client_organization'];
type OrganizationNetwork = NonNullable<Organization>['organization_networks'][0];
type Network = OrganizationNetwork['network'];

const NO_ACO_KEY = 'no-aco';

const DCE_FIELD_LABELS: Record<EditableNetworkMergeVar, string> = {
  dce_address1: 'Address line 1',
  dce_address2: 'Address line 2',
  dce_address3: 'Address line 3',
  dce_color: 'Color',
  dce_logo: 'Logo',
  dce_name: 'ACO Display Name',
  dce_phone: 'Phone',
  dce_provider_contact: 'Providers Contact',
  dce_url: 'Website',
};

interface AcoGroup {
  canonicalNetwork: Network;
  key: string;
  dc_id: string | null;
  networks: Network[];
}

const pageStyle: CSSProperties = {
  maxWidth: 960,
};

const contentCardStyle: CSSProperties = {
  border: '1px solid #e5e7eb',
  borderRadius: 8,
  boxShadow: '0 8px 24px rgba(15, 23, 42, 0.06)',
};

const organizationHeaderStyle: CSSProperties = {
  alignItems: 'flex-start',
  display: 'flex',
  flexWrap: 'wrap',
  gap: 24,
  justifyContent: 'space-between',
};

const organizationCardGridStyle: CSSProperties = {
  display: 'grid',
  gap: '18px 32px',
  gridTemplateColumns: 'minmax(0, 2fr) minmax(0, 1.4fr) minmax(100px, auto)',
};

const acoFieldGridStyle: CSSProperties = {
  display: 'grid',
  gap: '8px 24px',
  gridTemplateColumns: 'minmax(0, 2fr) minmax(0, 1.4fr) minmax(100px, auto)',
  marginTop: 10,
};

const acoColumnStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 8,
};

const pendingControlStyle: CSSProperties = {
  borderRadius: 4,
  outline: '2px solid #d4b106',
  outlineOffset: 1,
};

const withPendingBorder = (pending: boolean): CSSProperties => ({
  fontSize: 14,
  padding: '0 4px',
  ...(pending ? pendingControlStyle : {}),
});

const getLabelValue = (label: string, value?: React.ReactNode) => {
  if (value === undefined || value === null || value === '') return null;

  return (
    <div>
      <Typography.Text type="secondary">{label}</Typography.Text>
      <div style={{ fontSize: 16, marginTop: 2 }}>{value}</div>
    </div>
  );
};

const AcoCard: React.FC<{
  aco: AcoGroup;
  organizationId: string;
}> = ({ aco, organizationId }) => {
  const [draft, setDraft] = useState<NetworkMergeVarDraft>();
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string>();
  const inFlightRef = useRef(false);
  const draftRef = useRef(draft);
  draftRef.current = draft;
  const canonicalNetworkRef = useRef(aco.canonicalNetwork);
  canonicalNetworkRef.current = aco.canonicalNetwork;

  const [createRequest] = useCreateClientDataChangeRequestMutation();
  const [updateRequest] = useUpdateClientDataChangeRequestMutation();
  const [deleteRequest] = useDeleteClientDataChangeRequestMutation();

  const editable = Boolean(aco.dc_id);
  const currentDraft =
    draft || getNetworkMergeVarBaseline(aco.canonicalNetwork);
  const pendingRequest = getPendingNetworkRequest(aco.canonicalNetwork);
  const underReview = pendingRequest?.status === 'reviewing';
  const pendingChanges = requestToNetworkMergeVarChanges(pendingRequest);
  const liveValues = getLiveNetworkMergeVarValues(aco.canonicalNetwork);

  const setDraftField = (field: EditableNetworkMergeVar, value: string) => {
    const next = { ...currentDraft, [field]: value };
    draftRef.current = next;
    setDraft(next);
    return next;
  };

  const commit = (draftOverride?: NetworkMergeVarDraft) => {
    const nextDraft = draftOverride || draftRef.current;
    if (!nextDraft || !editable || underReview || inFlightRef.current) return;

    const network = canonicalNetworkRef.current;
    const changes = draftToNetworkMergeVarChanges(network, nextDraft);
    const hasChanges = Object.keys(changes).length > 0;
    const pending = getPendingNetworkRequest(network);
    if (
      networkMergeVarChangesEqual(
        changes,
        requestToNetworkMergeVarChanges(pending)
      )
    ) {
      if (Object.keys(changes).length === 0) setDraft(undefined);
      return;
    }

    let mutation: Promise<unknown>;
    if (pending && !hasChanges) {
      mutation = deleteRequest({
        variables: {
          data_change_request_id: pending.data_change_request_id,
        },
        refetchQueries: ['getClientOrganizationDetails'],
        awaitRefetchQueries: true,
      });
    } else if (pending) {
      mutation = updateRequest({
        variables: {
          data_change_request_id: pending.data_change_request_id,
          original_values: {
            network_merge_vars: originalValuesForNetworkMergeVarChanges(
              network,
              changes
            ),
          },
          proposed_changes: { network_merge_vars: changes },
        },
        refetchQueries: ['getClientOrganizationDetails'],
        awaitRefetchQueries: true,
      });
    } else {
      mutation = createRequest({
        variables: {
          request: {
            change_type: 'update',
            client_organization_id: organizationId,
            original_values: {
              network_merge_vars: originalValuesForNetworkMergeVarChanges(
                network,
                changes
              ),
            },
            proposed_changes: { network_merge_vars: changes },
            target_key: { network_id: network.network_id },
            target_table: 'networks',
          },
        },
        refetchQueries: ['getClientOrganizationDetails'],
        awaitRefetchQueries: true,
      });
    }

    inFlightRef.current = true;
    setSaving(true);
    setSaveError(undefined);
    mutation
      .then(() => {
        inFlightRef.current = false;
        setSaving(false);
        const latestDraft = draftRef.current;
        if (!latestDraft) return;
        if (networkMergeVarDraftsEqual(latestDraft, nextDraft)) {
          draftRef.current = undefined;
          setDraft(undefined);
        } else {
          commit(latestDraft);
        }
      })
      .catch((error: { message?: string }) => {
        inFlightRef.current = false;
        setSaving(false);
        setSaveError(error.message || 'Save failed');
      });
  };

  const revertField = (field: EditableNetworkMergeVar) => {
    const next = { ...currentDraft, [field]: liveValues[field] };
    draftRef.current = next;
    setDraft(next);
    commit(next);
  };

  const withdrawRequest = () => {
    draftRef.current = liveValues;
    setDraft(liveValues);
    commit(liveValues);
  };

  const displayChangeValue = (
    field: EditableNetworkMergeVar,
    value?: string | null
  ) => {
    if (field === 'dce_logo') return value ? 'Logo' : null;
    return value || null;
  };

  const editableField = (
    label: string,
    field: EditableNetworkMergeVar,
    prefix?: React.ReactNode
  ) => {
    const pending = field in pendingChanges;
    return (
      <div>
        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
          {label}
        </Typography.Text>
        <div style={{ alignItems: 'center', display: 'flex' }}>
          {prefix}
          <Input
            aria-label={label}
            bordered={false}
            className="editable-field"
            disabled={underReview}
            onBlur={() => commit()}
            onChange={(event) => setDraftField(field, event.target.value)}
            onPressEnter={(event) => (event.target as HTMLInputElement).blur()}
            style={withPendingBorder(pending)}
            value={currentDraft[field]}
          />
          {pending && !underReview ? (
            <Tooltip
              title={`Revert to current value: ${liveValues[field] || 'empty'}`}
            >
              <Button
                aria-label={`Revert ${label}`}
                icon={<UndoOutlined />}
                onClick={() => revertField(field)}
                size="small"
                style={{ marginLeft: 4 }}
                type="text"
              />
            </Tooltip>
          ) : null}
        </div>
      </div>
    );
  };

  const hasPendingChanges = Object.keys(pendingChanges).length > 0;

  return (
    <Card bodyStyle={{ padding: '14px 20px 16px' }} style={contentCardStyle}>
      <div style={organizationHeaderStyle}>
        <div style={{ flex: 1, minWidth: 260 }}>
          <div style={{ alignItems: 'center', display: 'flex', gap: 8 }}>
            {editable ? (
              <Typography.Text strong style={{ fontSize: 16 }}>
                {aco.canonicalNetwork.network_name}
              </Typography.Text>
            ) : (
              <Typography.Text strong style={{ fontSize: 16 }}>
                Other Networks
              </Typography.Text>
            )}
            {aco.dc_id ? <Tag>{aco.dc_id}</Tag> : null}
          </div>
          {!editable ? (
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              Networks not associated with an ACO entity
            </Typography.Text>
          ) : null}
          {saveError ? (
            <Typography.Text
              type="danger"
              style={{ display: 'block', fontSize: 12 }}
            >
              {saveError}
            </Typography.Text>
          ) : null}
        </div>
        {editable ? (
          <DataChangeRequestStatus
            error={saveError}
            pending={hasPendingChanges}
            pendingContent={
              hasPendingChanges ? (
                <Space direction="vertical">
                  <div>
                    {EDITABLE_NETWORK_MERGE_VARS.filter(
                      (field) => field in pendingChanges
                    ).map((field) => (
                      <div key={field}>
                        <Typography.Text type="secondary">
                          {DCE_FIELD_LABELS[field]}:{' '}
                        </Typography.Text>
                        <DataChangeRequestValue
                          previous
                          value={displayChangeValue(field, liveValues[field])}
                        />{' '}
                        <ArrowRightOutlined style={{ fontSize: 10 }} />{' '}
                        <DataChangeRequestValue
                          value={displayChangeValue(
                            field,
                            pendingChanges[field]
                          )}
                        />
                      </div>
                    ))}
                  </div>
                  {pendingRequest?.submission_note ? (
                    <Typography.Text type="secondary">
                      Note: {pendingRequest.submission_note}
                    </Typography.Text>
                  ) : null}
                  {!underReview ? (
                    <Button danger onClick={withdrawRequest} size="small">
                      Withdraw request
                    </Button>
                  ) : null}
                </Space>
              ) : undefined
            }
            retry={() => commit(currentDraft)}
            reviewing={underReview}
            saving={saving}
          />
        ) : null}
      </div>

      {editable ? (
        <div style={acoFieldGridStyle}>
          <div style={acoColumnStyle}>
            {editableField('ACO Display Name', 'dce_name')}
            {editableField('Address line 1', 'dce_address1')}
            {editableField('Address line 2', 'dce_address2')}
            {editableField('Address line 3', 'dce_address3')}
            {editableField('Providers Contact', 'dce_provider_contact')}
          </div>
          <div style={acoColumnStyle}>
            {editableField('Phone (for patient inquiries)', 'dce_phone')}
            {editableField('Website', 'dce_url')}
          </div>
          <div style={acoColumnStyle}>
            <div>
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                Logo
              </Typography.Text>
              <EditableDataChangeRequestLogo
                currentLogoUrl={liveValues.dce_logo}
                disabled={underReview}
                label={currentDraft.dce_name || aco.dc_id || 'ACO'}
                logoUrl={currentDraft.dce_logo}
                onChange={(logoUrl) =>
                  commit(setDraftField('dce_logo', logoUrl || ''))
                }
                onRevert={() => revertField('dce_logo')}
                pending={'dce_logo' in pendingChanges}
                style={{ marginTop: 4 }}
                uploadPath={`network/${aco.canonicalNetwork.network_id}`}
              />
            </div>
            <div>
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                Color
              </Typography.Text>
              <div style={{ alignItems: 'center', display: 'flex', gap: 4 }}>
                <div style={withPendingBorder('dce_color' in pendingChanges)}>
                  <ColorInput
                    disabled={underReview}
                    value={currentDraft.dce_color}
                    onChange={(color) =>
                      commit(setDraftField('dce_color', color))
                    }
                  />
                </div>
                {'dce_color' in pendingChanges && !underReview ? (
                  <Tooltip
                    title={`Revert to current value: ${
                      liveValues.dce_color || 'empty'
                    }`}
                  >
                    <Button
                      aria-label="Revert Color"
                      icon={<UndoOutlined />}
                      onClick={() => revertField('dce_color')}
                      size="small"
                      type="text"
                    />
                  </Tooltip>
                ) : null}
              </div>
            </div>
          </div>
        </div>
      ) : null}

      <div style={{ marginTop: 10 }}>
        {editable ? (
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            Networks
          </Typography.Text>
        ) : null}
        <div style={{ marginTop: editable ? 4 : 0 }}>
          <Space wrap size={4}>
            {aco.networks.map((network) => {
              const isPrimaryNetwork =
                network.network_id === aco.canonicalNetwork.network_id &&
                editable;
              const networkTag = (
                <Tag
                  key={network.network_id}
                  style={{
                    backgroundColor: isPrimaryNetwork ? '#e6f7ff' : undefined,
                    border: isPrimaryNetwork ? '2px solid #1890ff' : undefined,
                    borderRadius: 12,
                    fontWeight: isPrimaryNetwork ? 500 : undefined,
                  }}
                >
                  {network.network_name}
                  {aco.dc_id ? ` (${aco.dc_id})` : ''}
                </Tag>
              );
              return isPrimaryNetwork ? (
                <Tooltip
                  key={network.network_id}
                  title={`Primary network for ${aco.dc_id}`}
                >
                  {networkTag}
                </Tooltip>
              ) : (
                networkTag
              );
            })}
          </Space>
        </div>
      </div>
    </Card>
  );
};

const ClientOrganizationProfilePage: React.FC = () => {
  const { user } = useContext(UserContext);
  const { data, loading, error } = useGetClientOrganizationDetailsQuery({
    variables: { user_id: user?.user_id },
    notifyOnNetworkStatusChange: true,
  });
  const organization = data?.users_by_pk?.client_organization;
  const organizationId = data?.users_by_pk?.client_organization_id;
  const organizationNetworks = organization?.organization_networks;

  const activeNetworks = useMemo(() => {
    return (
      organizationNetworks
        ?.filter((organizationNetwork) => {
          return (
            !organizationNetwork.is_archived &&
            !organizationNetwork.network.is_archived
          );
        })
        .map(({ network }) => network)
        // Oldest network first — it is the source of each ACO's variables below
        .sort(
          (a, b) =>
            new Date(a.created_at).getTime() -
              new Date(b.created_at).getTime() ||
            a.network_id.localeCompare(b.network_id)
        ) || []
    );
  }, [organizationNetworks]);

  const acoGroups = useMemo(() => {
    const groups = new Map<string, AcoGroup>();

    for (const network of activeNetworks) {
      const key = network.dc_id || NO_ACO_KEY;
      const existingGroup = groups.get(key);
      if (existingGroup) {
        existingGroup.networks.push(network);
        continue;
      }
      groups.set(key, {
        canonicalNetwork: network,
        key,
        dc_id: network.dc_id || null,
        networks: [network],
      });
    }

    // ACO cards sorted by dc_id, with any no-ACO group last
    return Array.from(groups.values()).sort((a, b) => {
      if (!a.dc_id) return 1;
      if (!b.dc_id) return -1;
      return a.dc_id.localeCompare(b.dc_id);
    });
  }, [activeNetworks]);

  if (loading && !data) return <Skeleton />;
  if (error)
    return (
      <Result
        status="error"
        title="There was an error retrieving organization details"
        subTitle={error?.message}
      />
    );
  if (!organization || !organizationId)
    return (
      <Result
        status="warning"
        title="No organization profile found"
        subTitle="Your account is not attached to a client organization."
      />
    );

  return (
    <>
      <Helmet>
        <title>ACO Entities | Mabel</title>
      </Helmet>
      <div style={pageStyle}>
        <Typography.Title level={2} style={{ marginBottom: 4 }}>
          ACO Entities
        </Typography.Title>
        <Typography.Paragraph type="secondary" style={{ fontSize: 16 }}>
          The ACOs associated with your organization.
        </Typography.Paragraph>

        <Card
          bodyStyle={{ padding: 24 }}
          style={{ ...contentCardStyle, marginBottom: 24 }}
        >
          <div style={organizationCardGridStyle}>
            <div>
              <Typography.Text type="secondary">Organization</Typography.Text>
              <Typography.Title
                level={4}
                style={{ marginBottom: 0, marginTop: 2 }}
              >
                {organization.organization_name}
              </Typography.Title>
              {organization.organization_description ? (
                <Typography.Paragraph
                  style={{ marginBottom: 0, marginTop: 8, maxWidth: 640 }}
                >
                  {organization.organization_description}
                </Typography.Paragraph>
              ) : null}
            </div>
            {getLabelValue('Organization ID', organization.organization_id)}
            {getLabelValue(
              'ACO Entities',
              acoGroups.filter((group) => group.dc_id).length
            )}
          </div>
        </Card>

        {acoGroups.length ? (
          <Space direction="vertical" size={16} style={{ width: '100%' }}>
            {acoGroups.map((aco) => (
              <AcoCard
                aco={aco}
                key={aco.key}
                organizationId={organizationId}
              />
            ))}
          </Space>
        ) : (
          <Card style={contentCardStyle}>
            <Empty description="No active networks are attached to this organization." />
          </Card>
        )}

        <Typography.Paragraph
          type="secondary"
          style={{ fontSize: 14, marginBottom: 0, marginTop: 24 }}
        >
          Edits are submitted to Mabel for review before they take effect.
          Questions? Contact{' '}
          <a href="mailto:support@getmabel.com">support@getmabel.com</a>
        </Typography.Paragraph>
      </div>
    </>
  );
};

export default ClientOrganizationProfilePage;
